import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, X } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger'
}) => {
  const { t } = useLanguage();
  const { theme } = useTheme();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        <motion.div
           initial={{ opacity: 0, scale: 0.95, y: 20 }}
           animate={{ opacity: 1, scale: 1, y: 0 }}
           exit={{ opacity: 0, scale: 0.95, y: 20 }}
           className={`relative w-full max-w-md ${theme === 'dark' ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-100'} rounded-[3rem] shadow-2xl border overflow-hidden transition-colors`}
         >
           <div className="p-6 sm:p-8">
             <div className="flex items-center justify-between mb-8">
               <div className={`p-4 rounded-2xl ${
                 variant === 'danger' ? 'bg-red-50 dark:bg-red-900/20 text-red-600' :
                 variant === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600' :
                 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'
               }`}>
                 <AlertCircle size={28} />
               </div>
               <button 
                 onClick={onClose}
                 className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 dark:text-zinc-300"
               >
                 <X size={20} />
               </button>
             </div>

             <h3 className={`text-xl sm:text-2xl font-black uppercase tracking-tighter mb-2 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>
               {title}
             </h3>
             <p className={`font-bold leading-relaxed text-sm ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}`}>
               {message}
             </p>
           </div>

           <div className="p-6 sm:p-8 pt-0 flex flex-col sm:flex-row gap-4">
             <button
               onClick={onClose}
               className={`flex-1 px-6 py-4 ${theme === 'dark' ? 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:bg-zinc-800' : 'bg-zinc-50 text-zinc-500 border-zinc-100 hover:bg-zinc-100'} rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all border`}
             >
               {cancelLabel || t('common.cancel')}
             </button>
             <button
               onClick={() => {
                 onConfirm();
                 onClose();
               }}
               className={`flex-1 px-6 py-4 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all shadow-xl ${
                 variant === 'danger' ? 'bg-red-600 hover:bg-red-500 shadow-red-500/20' :
                 variant === 'warning' ? 'bg-amber-600 hover:bg-amber-500 shadow-amber-500/20' :
                 'bg-zinc-900 dark:bg-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 shadow-zinc-200'
               }`}
             >
               {confirmLabel || t('common.confirm')}
             </button>
           </div>
         </motion.div>
      </div>
    </AnimatePresence>
  );
};
